import { Parser, isBase58ByteWidth, SOLANA_CHAIN_REFERENCE } from "@repo/caip";
import { lazySchema } from "@repo/lazy-schema";
import { isAddress } from "viem";
import { z } from "zod";
import { ExecutionPolicySchema } from "./execution-policy.js";
import {
  ExecutionCorrelationIdSchema,
  ExecutionPortfolioIdSchema,
} from "./requests.js";

const account = (value: string): boolean => {
  try {
    const parsed = Parser.parseAccountId(value);
    return parsed.chain.namespace === "eip155"
      ? /^[1-9]\d*$/u.test(parsed.chain.reference) &&
          isAddress(parsed.account.address)
      : parsed.chain.namespace === "solana" &&
          parsed.chain.reference === SOLANA_CHAIN_REFERENCE &&
          isBase58ByteWidth(parsed.account.address, 32);
  } catch {
    return false;
  }
};
const assetKey = (value: string): string | null => {
  try {
    const parsed = Parser.parseAssetId(value);
    const { namespace, reference } = parsed.asset;
    if (
      parsed.chain.namespace === "eip155" &&
      /^[1-9]\d*$/u.test(parsed.chain.reference) &&
      ((namespace === "erc20" && isAddress(reference)) ||
        (namespace === "slip44" && reference === "60"))
    )
      return value.toLowerCase();
    if (
      parsed.chain.namespace === "solana" &&
      parsed.chain.reference === SOLANA_CHAIN_REFERENCE &&
      ((namespace === "spl" && isBase58ByteWidth(reference, 32)) ||
        (namespace === "slip44" && reference === "501"))
    )
      return value;
    return null;
  } catch {
    return null;
  }
};
const positiveRaw = lazySchema(() =>
  z
    .string()
    .regex(/^[1-9]\d{0,77}$/u)
    .refine(
      (value) => /^[1-9]\d{0,77}$/u.test(value) && BigInt(value) < 1n << 256n,
    ),
);

/** Exact authorized selection supplied only by an authenticated internal service.
 * The source authorization stays with that service; its digest binds retries
 * without introducing API-specific signature or tenant semantics in execution. */
export const exactSelectionWithdrawalIntentSchema = lazySchema(() =>
  z
    .object({
      kind: z.literal("withdraw_exact_selection"),
      recipientAccountId: z.string().refine(account),
      assets: z
        .array(
          z
            .object({
              assetId: z.string().refine((value) => assetKey(value) !== null),
              amountRaw: positiveRaw,
            })
            .strict(),
        )
        .min(1)
        .max(50),
      expiresAt: z.number().int().positive().safe(),
      settlementAssetId: z
        .string()
        .refine((value) => assetKey(value) !== null)
        .optional(),
    })
    .strict()
    .superRefine((intent, ctx) => {
      const chainRef = intent.recipientAccountId.slice(
        0,
        intent.recipientAccountId.lastIndexOf(":"),
      );
      const keys = intent.assets.map((asset) => assetKey(asset.assetId));
      if (
        new Set(keys).size !== keys.length ||
        intent.assets.some(
          (asset) => asset.assetId.split("/")[0] !== chainRef,
        ) ||
        (intent.settlementAssetId &&
          intent.settlementAssetId.split("/")[0] !== chainRef)
      )
        ctx.addIssue({
          code: "custom",
          message: "Recipient chain and unique asset selection must agree",
        });
    }),
);

export const exactSelectionWithdrawalIdentitySchema = lazySchema(() =>
  z
    .object({
      portfolioId: ExecutionPortfolioIdSchema,
      requestKey: z.string().min(1).max(256),
      authorizationDigest: z.string().regex(/^[0-9a-f]{64}$/u),
      intent: exactSelectionWithdrawalIntentSchema,
    })
    .strict(),
);

/** The resolved policy is trusted internal-service input. It is frozen into
 * admitted actions, but is not a mutable prerequisite to accepted replay. */
export const exactSelectionWithdrawalCreateSchema = lazySchema(() =>
  exactSelectionWithdrawalIdentitySchema
    .extend({
      authority: z
        .object({
          ownerAddress: z.string().min(1).max(200),
          executionAccountRef: z.string().refine(account),
        })
        .strict(),
      executionPolicy: ExecutionPolicySchema.optional(),
      correlationId: ExecutionCorrelationIdSchema.optional(),
    })
    .strict(),
);
export type ExactSelectionWithdrawalIdentity = z.infer<
  typeof exactSelectionWithdrawalIdentitySchema
>;
export type ExactSelectionWithdrawalCreate = z.infer<
  typeof exactSelectionWithdrawalCreateSchema
>;
export const exactSelectionWithdrawalAckSchema = lazySchema(() =>
  z
    .object({
      operationId: z.string().min(1),
      portfolioId: ExecutionPortfolioIdSchema,
      requestKey: z.string().min(1),
      submittedAt: z.iso.datetime(),
    })
    .strict(),
);
export type ExactSelectionWithdrawalAck = z.infer<
  typeof exactSelectionWithdrawalAckSchema
>;
export const exactSelectionWithdrawalInputFingerprint = (
  input: ExactSelectionWithdrawalIdentity,
): string =>
  JSON.stringify({
    domain: "exact-selection-withdrawal-v1",
    ...exactSelectionWithdrawalIdentitySchema.parse({
      portfolioId: input.portfolioId,
      requestKey: input.requestKey,
      authorizationDigest: input.authorizationDigest,
      intent: input.intent,
    }),
  });
